/**
 * DataSource for authz.user_role logical table
 * Tracks which users are assigned which roles
 */

import { DataSource, FilterSchema } from './DataSource';
import { queryGqlApi } from '../../graphql/queryGqlApi';
import { LIST_USER_ROLES, onCreateUserRole, onUpdateUserRole, onDeleteUserRole } from '../../graphql/rbac/userRoles';
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {IGraphQLSubscriptionClient} from "@stores/gqlModel/IGraphQLSubscriptionClient";

export interface UserRole {
    id: string;
    ownerId: string;
    userId: string;
    roleId: string;
    workspaceId: string;
    createdAt: string;
    createdBy?: string;
}

/**
 * DataSource for user-role assignments
 * Provides real-time sync via WebSocket subscriptions
 */
export class UserRolesDataSource extends DataSource<UserRole, UserRole, BasicDataSourceDeps> {
    readonly _modelName = 'UserRole';
    readonly _subscriptionGQLs = {
        onCreate: onCreateUserRole,
        onUpdate: onUpdateUserRole,
        onDelete: onDeleteUserRole,
    };
    
    async loadGQLFetch(): Promise<{ items: UserRole[], nextToken: string | null }> {
        return queryGqlApi(
            LIST_USER_ROLES,
            { limit: 1000 },
            this.deps.dataClientService,
            this.deps.authService,
            'pgServer'
        );
    }
    
    async loadMoreGQLFetch(nextToken: string): Promise<{ items: UserRole[], nextToken: string | null }> {
        return queryGqlApi(
            LIST_USER_ROLES,
            { limit: 1000, nextToken },
            this.deps.dataClientService,
            this.deps.authService,
            'pgServer'
        );
    }
    
    getWsClient(): Promise<IGraphQLSubscriptionClient | undefined> {
        return this.deps.dataClientService.getWsClientByKey('pgServer');
    }
    
    /**
     * Get all user-role assignments for a specific role
     */
    getUserRolesByRoleId(roleId: string): UserRole[] {
        return this.allRows.filter(ur => ur.roleId === roleId);
    }
    
    /**
     * Get all user-role assignments for a specific user
     */
    getUserRolesByUserId(userId: string): UserRole[] {
        return this.allRows.filter(ur => ur.userId === userId);
    }
    
    /**
     * Get all user-role assignments for a specific workspace
     */
    getUserRolesByWorkspaceId(workspaceId: string): UserRole[] {
        return this.allRows.filter(ur => ur.workspaceId === workspaceId);
    }
    
    /**
     * Check if a user has a specific role in a workspace
     */
    hasUserRole(userId: string, roleId: string, workspaceId: string): boolean {
        return this.allRows.some(
            ur => ur.userId === userId && ur.roleId === roleId && ur.workspaceId === workspaceId
        );
    }
    
    getFilterSchema(): FilterSchema<UserRole> {
        return {
            workspaceId: {
                type: 'client',
                apply: (userRole: UserRole) => this.filterValues.workspaceId === null || userRole.workspaceId === this.filterValues.workspaceId,
            },
            userId: {
                type: 'client',
                apply: (userRole: UserRole) => this.filterValues.userId === null || userRole.userId === this.filterValues.userId,
            },
        };
    }
    
    getFilterDefaultValues() {
        return {
            workspaceId: null,
            userId: null,
        };
    }
    
    get syncHash() {
        return '';
    }

    async refreshFromServer(): Promise<void> {
        this._latestRequestedFilterHash = undefined;
        this.isFullyLoaded = false;
        await this._applyServerFilter();
        if (this.nextToken) {
            await this._loadAllRows();
        }
    }
}
